import { Fragment, useCallback } from "react";
import type { FunctionComponent, ReactNode } from "react";
import { graphql, useFragment } from "react-relay";

import { BaseLinkContextProvider } from "shared/components/common/base-link/context";
import { tracking } from "shared/data";
import { useTrackImpressionEventByIntersection } from "shared/hooks";
import { getAbsoluteUrl } from "shared/lib/utils";

import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import type { RecirculationWidgetImpressionEvent } from "scmp-app/components/tracking/google-analytics-4/types";
import type { mostPopularHomeV2Query$key } from "scmp-app/queries/__generated__/mostPopularHomeV2Query.graphql";

import {
  Container,
  DisplayNumber,
  Divider,
  Item1,
  Item2,
  Item3,
  Item4,
  Item5,
  ItemContainer,
  StyledContentItem,
  Title,
} from "./styles";

type Props = {
  className?: string;
  reference: mostPopularHomeV2Query$key;
  trendingTopicSlot?: ReactNode;
};

export const HomeV2MostPopular: FunctionComponent<Props> = ({
  className,
  reference,
  trendingTopicSlot,
}) => {
  const data = useFragment(
    graphql`
      fragment mostPopularHomeV2Query on Query
      @argumentDefinitions(mostPopularQueueName: { type: "String!" }) {
        mostPopularV2Queue: queue(filter: { name: $mostPopularQueueName }) {
          items(first: 5) {
            edges {
              node {
                ... on Content {
                  entityId
                  ...homeSecondaryV2ContentItemContent
                }
              }
            }
          }
        }
      }
    `,
    reference,
  );

  const mostPopularItems = data?.mostPopularV2Queue?.items?.edges ?? [];
  const listOfArticles = mostPopularItems.map(item => item.node.entityId).join(",");

  const impressionGA4Event = useCallback<() => RecirculationWidgetImpressionEvent>(
    () => ({
      action: "imp",
      category: "recirculation",
      customized_parameters: {
        list_of_articles: listOfArticles,
        page_type: "homepage",
        widget_name: "most_popular",
      },
      subcategory: "widget",
    }),
    [listOfArticles],
  );

  const { captureTrackImpressionEventTargetElement } =
    useTrackImpressionEventByIntersection<RecirculationWidgetImpressionEvent>({
      ga4TrackingHandler: sendGA4Tracking,
      getGa4Event: impressionGA4Event,
      options: { isSendGA4Tracking: true, shouldSendOnce: true },
    });

  const itemComponents = [Item1, Item2, Item3, Item4, Item5];

  if (mostPopularItems.length === 0) return null;
  return (
    <BaseLinkContextProvider
      customQueryParameters={{
        module: tracking.module.MostPopular,
      }}
    >
      <Container className={className} ref={captureTrackImpressionEventTargetElement}>
        <Title>Most Popular</Title>
        <ItemContainer>
          {mostPopularItems.map((item, index) => {
            const Component = itemComponents[index];
            return (
              <Fragment key={item.node.entityId}>
                <Component>
                  <DisplayNumber>{index + 1}</DisplayNumber>
                  <StyledContentItem
                    onClick={(entityId, urlAlias) => {
                      sendGA4Tracking({
                        action: "click",
                        category: "recirculation",
                        customized_parameters: {
                          article_id: entityId,
                          destination_url: getAbsoluteUrl(urlAlias),
                          page_type: "homepage",
                          widget_name: "most_popular",
                        },
                        subcategory: "widget",
                      });
                    }}
                    reference={item.node}
                  />
                </Component>
                {index === 2 && <Divider />}
              </Fragment>
            );
          })}
        </ItemContainer>

        {trendingTopicSlot && <>{trendingTopicSlot}</>}
      </Container>
    </BaseLinkContextProvider>
  );
};

HomeV2MostPopular.displayName = "HomeV2MostPopular";
